/*
  Charts.JS

  Echart 모듈을 사용하는 컨텐츠의 공통 모듈 / 유틸을 정의한다.
*/

// TAMS
import {looseLabel, values} from '~/plugins/looseLabels'

/*
  Object - Level Color
  설명: 국정원 사이버 위기정보에서 사용하는 Level 색상
*/
const levelColor = ['#43CEC6', '#4c9ded', '#f4d200', '#f59b33', '#f12c40']
/*
  Object - Top5 Color
  설명: 세계 지도에서 사용하는 TOP5 색상
*/
const top5Color = ['#a42aca', '#c441db', '#da70e9', '#e7a5f1', '#f1c3fb']

/*
  Object - Top10 Color
  Description - Top10 차트의 컬러를 정의.
*/
const top10Color = ['#1883FA', '#10EBFF', '#cba8eb', '#8c80ec', '#a2b7fb', '#417FB4', '#18b8fa', '#0061cc', '#619df6', '#24d6c7']
const top10ColorArea = ['rgba(24, 131, 250, 0.5)', 'rgba(16, 235, 255, 0.5)', 'rgba(203, 168, 235, 0.5)', 'rgba(140, 128, 236, 0.5)', 'rgba(161, 183, 251, 0.5)', 'rgba(65, 127, 180, 0.5)', 'rgba(24, 184, 250, 0.5)', 'rgba(0, 97, 204, 0.5)', 'rgba(97, 157, 246, 0.5)', 'rgba(36, 214, 199, 0.5)']

/*
  Method - thousandsSeparators
  Description - 천단위마다 콤마로 구분. (소수점 지원)
*/
const thousandsSeparators = num => {
  const numParts = num.toString().split('.')
  numParts[0] = numParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return numParts.join('.')
}

/*
  Method - floorSecondTime
  Description - 시간 객체에서 1초단위를 버림처리한다.
              - Ex) 00:00:15 -> 00:00:10
*/

const floorSecondTime = function (seconds) {
  // 10으로 나누기
  const remainderSeconds = seconds % 10

  // 현재 값 - 나머지로 뒷자리를 0으로 만든다
  return seconds - remainderSeconds
}

/*
  Method - bpsConverter
  Description - bps 단위를 값에 따라 bps, Kbps, Mbps, Gbps, Tbps로 변환.
*/
function bpsConverter (bps, fixed = 1) {
  let i = 0
  const byteUnits = [' bps', ' kbps', ' Mbps', ' Gbps', ' Tbps', ' Pbps', ' Ebps', ' Zbps', ' Ybps']

  while (bps >= 1000) {
    bps = bps / 1000
    i++
  }
  return _.round(bps ? bps.toFixed(fixed) : 0, fixed) + byteUnits[i] // TAMS
}

/*
  Method - ppsConverter
  Description - pps 단위를 값에 따라 pps, kpps, Mpps, Gpps, Tpps로 변환.
*/
function ppsConverter (pps, fixed = 1) {
  let i = 0
  const byteUnits = [' pps', ' kpps', ' Mpps', ' Gpps', ' Tpps', ' Ppps', ' Epps', ' Zpps', ' Ypps']

  while (pps >= 1000) {
    pps = pps / 1000
    i++
  }

  return _.round(pps ? pps.toFixed(fixed) : 0, fixed) + byteUnits[i] // TAMS
}

/*
  Method - byteConverter
  Description - 바이트(1000) 단위를 값에 따라 KB, MG, GB, TB, PB로 변환, Prefix를 붙여 문자열을 리턴.
                * TAMS 수정됨 (suffix)
*/
function byteConverter (bytes, fixed = 2, suffix = 'B') {
  if (bytes === 0) {
    return 0
  }
  bytes = parseFloat(bytes)
  var s = [' ', 'k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y', 'B']
  var e = Math.floor(Math.log(bytes) / Math.log(1000))
  if (e < 1) {
    return _.round(bytes.toFixed(fixed), fixed) + s[0] + suffix
  } else if (!isFinite(e)) {
    return '0 ' + s[0] + suffix
  } else {
    return thousandsSeparators(_.round((bytes / Math.pow(1000, Math.floor(e))).toFixed(fixed), fixed)) + ' ' + (s[e] || '') + suffix
  }
}

/*
  Method - separateByComma
  Description - 천단위마다 콤마로 구분.
*/
const separateByComma = function (x) {
  return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0
}

/* ======================================
 * TAMS 정의 함수
 * ====================================== */

/*
  Method - fileSizeConverter
  Description - 파일사이즈 계산을 위함 (1000 byte)
*/
function fileSizeConverter (bytes, fixed = 2) {
  if (bytes === 0) {
    return 0
  }
  bytes = parseFloat(bytes)
  var s = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB', 'BB']
  var e = Math.floor(Math.log(bytes) / Math.log(1000))
  if (e < 1) return _.round(bytes.toFixed(fixed), fixed) + s[0]
  else if (!isFinite(e)) return '0 ' + s[0]
  else return thousandsSeparators(_.round((bytes / Math.pow(1000, Math.floor(e))).toFixed(fixed), fixed)) + ' ' + (s[e] || '')
}

/*
  Method - kmgtConverter
  Description - 1000 단위에 따라 k, M, G, T, P로 변환, Prefix를 붙여 문자열을 리턴.
*/
function kmgtConverter (value, fixed = 2, suffix = '') {
  if (value === 0) {
    return 0
  }
  value = parseFloat(value)
  var s = [' ', 'k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y', 'B']
  var e = Math.floor(Math.log(value) / Math.log(1000))
  if (e < 1) return _.round(value.toFixed(fixed), fixed) + s[0] + suffix
  else if (!isFinite(e)) return '0 ' + s[0] + suffix
  else return thousandsSeparators(_.round((value / Math.pow(1000, Math.floor(e))).toFixed(fixed), fixed)) + ' ' + (s[e] || '') + suffix
}

/*
  Method - getColorLuminance
  Description - 주어진 컬러의 명도 값에 따라 텍스트 색상(White/Black)을 반환한다.
 */
const getColorLuminance = color => {
  const regexp = /^(#(?:[0-9a-fA-F]{2}){2,4}|(#[0-9a-fA-F]{3}))$/
  let hexColor = ''
  if (regexp.test(color)) {
    if (color.toString().indexOf('#') === 0) {
      hexColor = color.substr(1, color.length)
    }

    const r = parseInt(hexColor.substr(0, 2), 16),
      g = parseInt(hexColor.substr(2, 2), 16),
      b = parseInt(hexColor.substr(4, 2), 16),
      yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000

    return (yiq >= 128) ? 'black' : 'white'
  }
}

function packetConverter (pkts, fixed = 1) {
  pkts = Number(pkts)
  if (!_.isNumber(pkts)) return '0'
  if (pkts === 0) {
    return 0
  }
  let i = 0
  const units = ['', ' k', ' M', ' G', ' T', ' P', ' E', ' Z', ' Y']

  while (pkts >= 1000) {
    pkts = pkts / 1000
    i++
  }

  return pkts.toFixed(pkts ? fixed : 0) + units[i]
}

// 트래픽 차트용 0 Line 중앙 정렬
// ex) Allow/Deny Trend Chart, RX/TX Trend Chart
function trafficTickPositioner (dataMin, dataMax) {
  try {
    const
      absDataMax = Math.max(Math.abs(dataMin), Math.abs(dataMax)),
      fixAbsDataMax = absDataMax === 0 ? 100 : absDataMax, // 최소값 100 보정
      looseLabels = values(looseLabel(0, fixAbsDataMax, 4))
    if (!(_.get(looseLabels, ['length'], 0) > 0)) return void 0
    let res = []
    for (let i = looseLabels.length; i > 0; i--) {
      const target = looseLabels[i - 1]
      if (target !== 0) {
        res.push(-target)
      }
    }
    res = res.concat(looseLabels)
    return res
  } catch (e) {
    return void 0
  }
}

export {
  levelColor,
  top5Color,
  top10Color,
  top10ColorArea,
  bpsConverter,
  ppsConverter,
  byteConverter,
  separateByComma,
  floorSecondTime,

  // TAMS
  fileSizeConverter,
  kmgtConverter,
  thousandsSeparators,
  getColorLuminance,
  packetConverter,
  trafficTickPositioner
}
